import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Alert, Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { gateway } from "../../../../../../gateway";

type Status = {
  canCharge: boolean;
  pending: boolean;
  balance: number;
  currency: string;
  card: { brand: string | null; last4: string | null } | null;
};

interface Props {
  invoiceUid: string;
  onCharged: () => void;
}

/** Staff view only. The server resolves the client, amount and Stripe payment method. */
export function InvoiceSavedCardCharge({ invoiceUid, onCharged }: Props) {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [charging, setCharging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    gateway
      .get<Status>(`/invoices/${invoiceUid}/saved-card`)
      .then((response) => {
        if (active) setStatus(response.data);
      })
      .catch(() => {
        if (active) setStatus(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [invoiceUid]);

  if (loading) return <CircularProgress size={20} />;
  if (!status?.canCharge && !status?.pending) return null;

  const amount = new Intl.NumberFormat("en-US", { style: "currency", currency: status.currency }).format(status.balance);
  const cardName = status.card ? `${status.card.brand || "Card"} ending ${status.card.last4 || "••••"}` : "saved card";

  const charge = async () => {
    setConfirmOpen(false);
    setCharging(true);
    setMessage(null);
    try {
      const response = await gateway.post<{ status: string }>(`/invoices/${invoiceUid}/saved-card/charge`, {});
      setMessage(
        response.data.status === "succeeded" ? "Payment completed." : "Payment is processing. Refresh the invoice to check its final status.",
      );
      onCharged();
      const refreshed = await gateway.get<Status>(`/invoices/${invoiceUid}/saved-card`);
      setStatus(refreshed.data);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || "The card could not be charged. Ask the client to use the invoice payment link.");
    } finally {
      setCharging(false);
    }
  };

  return (
    <Card sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <CreditCardIcon />
        <Typography variant="h6">Charge saved client card</Typography>
      </Box>
      {status.pending ? (
        <Alert severity="info">An invoice payment is already pending. Check its status before starting another charge.</Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {cardName} · Balance due: {amount}
          </Typography>
          <Button variant="contained" disabled={charging} onClick={() => setConfirmOpen(true)}>
            {charging ? "Charging…" : `Charge ${amount}`}
          </Button>
        </>
      )}
      {message && (
        <Alert severity={message === "Payment completed." ? "success" : "warning"} sx={{ mt: 1.5 }}>
          {message}
        </Alert>
      )}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        sx={{
          zIndex: 99999,
        }}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 99999,
            },
          },
        }}
        PaperProps={{
          sx: {
            position: "relative",
            zIndex: 99999,
          },
        }}
      >
        <DialogTitle>Confirm card charge</DialogTitle>

        <DialogContent>
          <Typography>
            Charge {amount} to the client's {cardName} for this invoice?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>

          <Button onClick={charge} color="primary" variant="contained">
            Confirm charge
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
